import { useEffect, useState } from "react";
import { useClient } from "../../hooks/ClientProvider";
import useCheckPermission from "../../utils/useCheckPermission";
import { Installment, InternalInstallment, Parameters } from "./types";
import { InvoiceInstallmentService } from "../../services/invoiceInstallmentService";
import { notifyError, notifySucces } from "../../utils/notifications";
import { isCustomError } from "../../utils/validations";
import api from "../../services/api";

type UseInvoiceInstallmentSimulatorFlowProps = {
  devedorTotal?: boolean;
  onClose: () => void;
};

export function useInvoiceInstallmentSimulatorFlow({
  devedorTotal = false,
  onClose,
}: UseInvoiceInstallmentSimulatorFlowProps) {
  const { client } = useClient();

  const clientId = client.client.id;
  const clientInfos = {
    creditCardNumber: client?.information?.numeroCartaoTruncado,
    currentInvoiceValue: client?.client?.valorFaturaFechada,
    debitBalance: client?.client?.devedorTotal,
    name: client?.client?.name,
  };

  const hasPermissionToChangeTax = useCheckPermission(
    "invoice_installment_tax"
  );

  const [isLoadingSimulate, setIsLoadingSimulate] = useState(false);
  const [installmentOptions, setInstallmentOptions] = useState<Installment[]>(
    []
  );
  const [selectedInstallmentOption, setSelectedInstallmentOption] =
    useState<InternalInstallment>();

  const [isLoadingParameters, setIsLoadingParameters] = useState(false);
  const [parameters, setParameters] = useState<Parameters>();
  const [step, setStep] = useState(0);
  const [isLoadingActivate, setIsLoadingActivate] = useState(false);

  const onSelectInstallmentOption = (installment: Installment) => {
    setSelectedInstallmentOption((prev) => ({
      tax: prev?.tax,
      initialValue: prev?.initialValue,
      ...installment,
    }));
  };

  const onSimulateClickHandler = async ({
    tax,
    initialValue,
  }: {
    tax: number;
    initialValue?: number;
  }) => {
    try {
      setIsLoadingSimulate(true);
      const method = devedorTotal
        ? InvoiceInstallmentService.simulateTotal
        : InvoiceInstallmentService.simulate;

      setInstallmentOptions([]);
      setSelectedInstallmentOption(undefined);

      const response = await method({
        clientId,
        tax,
        initialValue,
      });

      if (devedorTotal && !response?.length) {
        return notifyError(
          "Cliente não possui devedor futuro!",
          "",
          undefined,
          true
        );
      }
      setInstallmentOptions(response);
      setSelectedInstallmentOption((prev) => ({
        ...prev,
        tax,
        initialValue,
      }));
    } catch (e) {
      const err = e as ErrorMessage;
      console.error("Error to get simulation", err);
      notifyError(err.message, "", undefined, true);
    } finally {
      setIsLoadingSimulate(false);
    }
  };

  const onSubmitInvoiceInstallmentClickHandler = async () => {
    try {
      setIsLoadingActivate(true);
      const response = await InvoiceInstallmentService.activate({
        devedorTotal,
        clientId,
        params: selectedInstallmentOption,
      });

      const hasCustomError = isCustomError(response);

      if (hasCustomError) {
        return notifyError(response.message, "", false, true);
      }

      notifySucces(response.message, true);
      onClose();
    } catch (e) {
      const err = e as ErrorMessage;
      console.error("Error to activate installment", err);
      notifyError(err.message, "", undefined, true);
    } finally {
      setIsLoadingActivate(false);
    }
  };

  const goBack = () => {
    setStep((prevState) => prevState - 1);
  };

  const goNext = () => {
    if (step === 0) {
      return setStep((prevState) => prevState + 1);
    }

    return onSubmitInvoiceInstallmentClickHandler();
  };

  const getParameters = async (devedorTotal: boolean) => {
    try {
      setIsLoadingParameters(true);
      const response = await api.get(`installment/parameters`, {
        devedorTotal,
      });

      const { data } = response;

      setParameters(data);
      onSimulateClickHandler({ tax: data.valorPadrao });
    } catch (err) {
      const error = err as ErrorMessage;
      console.error("Error to get the parameters", err);
      notifyError(error.message, "", undefined, true);
      onClose();
    } finally {
      setIsLoadingParameters(false);
    }
  };

  useEffect(() => {
    getParameters(devedorTotal);
  }, [devedorTotal]);

  return {
    isLoadingParameters,
    step,
    parameters,
    installmentOptions,
    selectedInstallmentOption,
    hasPermissionToChangeTax,
    isLoadingSimulate,
    onSimulateClickHandler,
    onSelectInstallmentOption,
    isLoadingActivate,
    goBack,
    goNext,
    clientInfos,
  };
}
